import { useState } from "react";
import { MapPin, Users } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { bazaarApiService } from "@/lib/bazaarApi";
import { useToast } from "@/hooks/use-toast";
import { logger } from "@/lib/logger";

interface BoothApplicationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  boothId: string;
  boothNumber: number | string;
  attendees: Array<{ name: string; email: string; individualID?: string }>;
  boothSize: "2x2" | "4x4";
  durationWeeks: number;
}

export default function BoothApplicationDialog({
  open,
  onOpenChange,
  boothId,
  boothNumber,
  attendees,
  boothSize,
  durationWeeks,
}: BoothApplicationDialogProps) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    // Check if all attendees have both name and email
    const validAttendees = attendees.filter(
      (attendee) => attendee.name.trim() && attendee.email.trim()
    );

    if (validAttendees.length === 0) {
      toast({
        title: "Validation Error",
        description: "At least one attendee with name and email is required.",
        variant: "destructive",
      });
      return false;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    for (const attendee of validAttendees) {
      if (!emailRegex.test(attendee.email)) {
        toast({
          title: "Validation Error",
          description: `Invalid email format for ${attendee.name || "attendee"}.`,
          variant: "destructive",
        });
        return false;
      }
    }

    // Check for duplicate emails (case-insensitive)
    const emails = validAttendees.map((attendee) =>
      attendee.email.toLowerCase()
    );
    const uniqueEmails = new Set(emails);
    if (emails.length !== uniqueEmails.size) {
      toast({
        title: "Validation Error",
        description: "All attendees must have unique email addresses.",
        variant: "destructive",
      });
      return false;
    }

    // Check for missing IDs and collect all attendees without IDs
    // Check all attendees that have at least a name (so we can display them)
    const attendeesToCheck = attendees.filter(
      (attendee) => attendee.name && attendee.name.trim()
    );
    const attendeesWithoutID = attendeesToCheck.filter(
      (attendee) => !attendee.individualID
    );

    if (attendeesWithoutID.length > 0) {
      const firstNames = attendeesWithoutID
        .map((attendee) => {
          const trimmedName = attendee.name.trim();
          return trimmedName.split(" ")[0]; // Get first name only
        })
        .filter((name) => name.length > 0);

      if (firstNames.length > 0) {
        let namesList: string;
        if (firstNames.length === 1) {
          namesList = firstNames[0];
        } else if (firstNames.length === 2) {
          namesList = `${firstNames[0]} and ${firstNames[1]}`;
        } else {
          namesList = `${firstNames.slice(0, -1).join(", ")}, and ${firstNames[firstNames.length - 1]}`;
        }

        toast({
          title: "Validation Error",
          description: `Please upload an ID card for ${namesList}.`,
          variant: "destructive",
        });
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const validAttendees = attendees.filter(
        (attendee) => attendee.name.trim() && attendee.email.trim()
      );

      logger.info("Submitting booth application with data:", {
        boothId,
        attendees: validAttendees,
        boothSize,
        durationWeeks,
        locationPreference: `Booth ${boothNumber}`,
      });

      await bazaarApiService.applyToBooth(boothId, {
        attendees: validAttendees.map((attendee) => ({
          name: attendee.name,
          email: attendee.email,
          individualID: attendee.individualID || "",
        })),
        boothSize,
        durationWeeks, // Use the actual duration from the form
        locationPreference: `Booth ${boothNumber}`, // Use booth number as location preference
      });

      toast({
        title: "Application Submitted Successfully",
        description: `Your booth application for Booth ${boothNumber} has been submitted successfully!`,
      });

      onOpenChange(false);
    } catch (error) {
      logger.error("Error submitting booth application:", error);

      // Extract error message from the error object
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to submit your booth application. Please try again.";

      toast({
        title: "Application Submission Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Apply for Booth {boothNumber}
          </DialogTitle>
          <DialogDescription>
            Confirm your application details for this platform booth.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Booth Details */}
          <Card>
            <CardContent className="p-4">
              <h4 className="font-semibold mb-2">Booth Details</h4>
              <div className="space-y-1 text-sm text-muted-foreground">
                <p>
                  <strong>Booth Number:</strong> {boothNumber}
                </p>
                <p>
                  <strong>Booth Size:</strong> {boothSize}
                </p>
                <p>
                  <strong>Duration:</strong> {durationWeeks} week
                  {durationWeeks > 1 ? "s" : ""}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Attendees Summary */}
          <Card>
            <CardContent className="p-4">
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <Users className="h-4 w-4" />
                Attendees (
                {
                  attendees.filter((a) => a.name.trim() && a.email.trim())
                    .length
                }
                )
              </h4>
              <div className="space-y-2 text-sm text-muted-foreground">
                {attendees
                  .filter(
                    (attendee) => attendee.name.trim() && attendee.email.trim()
                  )
                  .map((attendee, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between"
                    >
                      <p>
                        <strong>{attendee.name}</strong> - {attendee.email}
                      </p>
                      {attendee.individualID ? (
                        <span className="text-green-600 text-xs">
                          ✓ ID Uploaded
                        </span>
                      ) : (
                        <span className="text-red-600 text-xs">
                          ⚠ ID Required
                        </span>
                      )}
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="min-w-[120px]"
          >
            {isSubmitting ? "Submitting..." : "Submit Application"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
